import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, Mail, User, Lock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "@/hooks/useTranslation";
import { getLoginPath, getHomePath } from "@/utils/localePaths";
import { getReadableErrorMessage } from "@/utils/errorHandler";

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL } from "@/utils/apiConfig";

function InvitationAcceptPage() {
  const { t, locale } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (!token) {
      setError(t('auth.invite.invalid_link'));
      setLoading(false);
      return;
    }
    
    fetchInvitationDetails();
  }, [token]);

  const fetchInvitationDetails = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/team/invitation/${token}`);
      setInvitationData(response.data);
      setError(null);
    } catch (err) {
      const errorMsg = getReadableErrorMessage(err, t, 'auth.invite.fetch_error');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (password !== confirmPassword) {
      toast.error(t('messages.passwords_not_match'));
      return;
    }
    
    if (password.length < 8) {
      toast.error(t('messages.password_min_chars'));
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/team/accept-invitation`, {
        token,
        first_name: firstName,
        last_name: lastName,
        password
      });
      
      // Auto-login via the shared session helper: fetches /auth/me (authoritative,
      //   build_user_response) and persists auth_token + full user to localStorage,
      //   so a reload lands on Case A instead of the previous memory-only setUser.
      await loginWithToken(response.data.access_token);

      toast.success(t('auth.invite.welcome'));
      navigate(getHomePath(locale));
    } catch (err) {
      const errorMsg = getReadableErrorMessage(err, t, 'auth.invite.accept_error');
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#FFDD59] mx-auto mb-4" />
          <p className="text-gray-400">{t('auth.invite.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !invitationData) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <Card className="bg-gray-900 border-gray-800 w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-400 text-center">{t('auth.invite.invalid_title')}</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-400">{error}</p>
            <Button
              onClick={() => navigate(getLoginPath(locale))}
              className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
            >
              {t('emailverify.btn_back_login')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <Card className="bg-gray-900 border-gray-800 w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-white text-center flex items-center justify-center gap-2">
            <CheckCircle className="h-6 w-6 text-[#FFDD59]" />
            {t('auth.invite.title')}
          </CardTitle>
          <CardDescription className="text-gray-400 text-center">
            {t('auth.invite.subtitle', { name: invitationData.admin_name })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {t('auth.login.email_label')}
              </label>
              <Input
                type="email"
                value={invitationData.email}
                disabled
                className="bg-gray-800 border-gray-700 text-gray-400"
              />
            </div>

            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <User className="h-4 w-4" />
                {t('auth.register.first_name')}
              </label>
              <Input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="bg-gray-800 border-gray-700 text-white"
                placeholder={t('auth.invite.first_name_placeholder')}
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <User className="h-4 w-4" />
                {t('auth.register.last_name')}
              </label>
              <Input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="bg-gray-800 border-gray-700 text-white"
                placeholder={t('auth.invite.last_name_placeholder')}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                {t('auth.login.password_label')}
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder={t('auth.password.min_length')}
              />
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                {t('auth.invite.password_confirm')}
              </label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder={t('auth.invite.password_confirm_placeholder')}
              />
            </div>

            {/* Info Box */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-sm text-blue-300">
                {t('auth.invite.info', { name: invitationData.admin_name })}
              </p>
            </div>

            {/* Expiry Info */}
            <p className="text-xs text-gray-500 text-center">
              {t('auth.invite.expiry', { date: new Date(invitationData.expires_at).toLocaleDateString('tr-TR') })}
            </p>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth.creating_account')}
                </>
              ) : (
                t('auth.invite.submit_btn')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default InvitationAcceptPage;
